import { AxiosProgressEvent } from 'axios';
import {
  ChatRequest,
  CreateTenderPackageRequest,
  GetDocumentsListRequest,
  GetEvaluationInsightsRequest,
  UpdateTenderPackageRequest,
  CreateTenderPackageForProjectRequest,
} from '../types/Dtos/request';
import {
  CreateTenderPackageResponse,
  GetTenderPackgeDetailsResponse,
} from '../types/Dtos/response';
import { api } from '../utils/api';
import { DocumentTypeEnum, ProxyEvaluationTypeEnum } from '../utils/constants';
import {
  deleteDocuments,
  getIndexingStatus,
  listDocuments,
  runAnalysis,
  uploadSingleDocument,
} from './documentsService';
import { connectToChatStream } from './chatService';
import {
  createEvaluation,
  deleteEvaluation,
  getEvaluationDetails,
  getEvaluationInsights,
  starEvaluationStatement,
  unstarEvaluationStatement,
  updateEvaluation,
} from './evaluationsService';

const RESOURCE_TYPE = DocumentTypeEnum.RFP; // 10

export const createTenderPackageForProject = ({
  payload,
  projectId,
  organizationId,
}: CreateTenderPackageForProjectRequest) =>
  createEvaluation(organizationId, {
    ...payload,
    projectId,
    resourceType: RESOURCE_TYPE,
  }) as Promise<import('axios').AxiosResponse<CreateTenderPackageResponse>>;

export const createTenderPackageForOrganization = (
  payload: CreateTenderPackageRequest,
  organizationId: string,
) =>
  createEvaluation(organizationId, {
    ...payload,
    resourceType: RESOURCE_TYPE,
  }) as Promise<import('axios').AxiosResponse<CreateTenderPackageResponse>>;

export const uploadTenderPackageDocument = (
  file: File,
  tenderPackageId: string,
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
) =>
  uploadSingleDocument(
    file,
    ProxyEvaluationTypeEnum.tenderPackage,
    tenderPackageId,
    undefined,
    onUploadProgress,
  );

export const getTenderPackageDetails = (tenderPackageId: string) =>
  getEvaluationDetails(tenderPackageId) as Promise<
    import('axios').AxiosResponse<GetTenderPackgeDetailsResponse>
  >;

export const analyseDocuments = (tenderPackageId: string) =>
  runAnalysis(ProxyEvaluationTypeEnum.tenderPackage, tenderPackageId);

export const connectToRfpChatStream = ({
  id,
  conversationId,
  userPrompt,
  specifiedDocuments,
  clarificationResponse,
}: ChatRequest) => {
  const body = {
    chat_session_id: `${conversationId}`,
    eval_id: id,
    eval_type: ProxyEvaluationTypeEnum.tenderPackage,
    query: userPrompt,
    specified_documents: specifiedDocuments,
    clarification_response: undefined,
  };
  if (clarificationResponse) body.clarification_response = clarificationResponse;
  return connectToChatStream(id, ProxyEvaluationTypeEnum.tenderPackage, body, true);
};

export const updateTenderPackage = (payload: UpdateTenderPackageRequest, tenderPackageId: string) =>
  updateEvaluation(tenderPackageId, { ...payload }) as Promise<
    import('axios').AxiosResponse<CreateTenderPackageResponse>
  >;

export const deleteTenderPackage = (tenderPackageId: string) => deleteEvaluation(tenderPackageId);

export const exportTenderPackageRfpReport = (tenderPackageId: string) =>
  api.get<Blob>(`tenderpackages/${tenderPackageId}/overview/export`, {
    responseType: 'blob',
  });

export const deleteTenderPackageDocuments = (tenderPackageId: string, documentIds: string[]) =>
  deleteDocuments(ProxyEvaluationTypeEnum.tenderPackage, tenderPackageId, documentIds);

export const markTpStatementAsStarred = (tenderPackageId: string, statementId: string) =>
  starEvaluationStatement(tenderPackageId, statementId);

export const unmarkTpStatementAsStarred = (tenderPackageId: string, statementId: string) =>
  unstarEvaluationStatement(tenderPackageId, statementId);

export const getTenderPackageDocuments = ({
  id,
  orderBy,
  orderDirection,
  searchText,
}: GetDocumentsListRequest) =>
  listDocuments(ProxyEvaluationTypeEnum.tenderPackage, id, orderBy, orderDirection, searchText);

export const getTenderPackageInsights = ({
  evaluationId,
  ...payload
}: GetEvaluationInsightsRequest) => getEvaluationInsights({ evaluationId, ...payload });

export const getProxyTenderPackageIndexedDocumentsStatus = (tenderPackageId: string) =>
  getIndexingStatus(ProxyEvaluationTypeEnum.tenderPackage, tenderPackageId);
